import { useLayoutEffect, useRef } from "react";

/** The smallest whole number of CSS px that is also a whole number of device px. */
export const devicePixelGrid = (dpr: number): number => {
	for (let step = 1; step <= 8; step++) {
		if (Math.abs(step * dpr - Math.round(step * dpr)) < 1e-6) return step;
	}
	return 1;
};

/** How far to move an edge at `top` so it lands on that grid. */
export const devicePixelSnapShift = (top: number, dpr: number): number => {
	const step = devicePixelGrid(dpr);
	return Math.round(top / step) * step - top;
};

/**
 * Puts the top of a bottom-anchored element on a line that is a whole pixel both in CSS
 * and on the device, by trimming its margin-bottom. Chrome rounds a box to whole CSS px
 * before scaling, so at 125% only every 4th CSS px is a whole device pixel; a 1px hairline
 * anywhere else smears over two or three rows. Bottom-anchored, its place follows the
 * window height and is usually off-grid; the top-anchored hairlines happen to sit on it.
 * Layout, not a transform: a fractional translate is resampled on the GPU and blurs too.
 * Re-run on resize, which also fires when the zoom changes.
 */
export const useDevicePixelSnap = <T extends HTMLElement>() => {
	const ref = useRef<T>(null);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const snap = () => {
			element.style.marginBottom = "";
			const margin = parseFloat(getComputedStyle(element).marginBottom) || 0;
			const shift = devicePixelSnapShift(
				element.getBoundingClientRect().top,
				window.devicePixelRatio || 1,
			);
			// A smaller margin below moves the element down, and the other way round.
			if (shift) element.style.marginBottom = `${margin - shift}px`;
		};
		snap();
		window.addEventListener("resize", snap);
		return () => window.removeEventListener("resize", snap);
	}, []);

	return ref;
};
